import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, Pencil, X, Search, BookMarked, Package, MapPin } from "lucide-react";

const empty = { name: "", address: "", zip: "", city: "", country: "CH", kind: "both", notes: "" };

export default function AddressBook() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "dispatcher";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const fetchRows = async () => {
    const params = {};
    if (q) params.q = q;
    if (kindFilter) params.kind = kindFilter;
    if (isAdmin && selectedCustomer) params.customer_id = selectedCustomer;
    try {
      const { data } = await api.get("/address-book", { params });
      setRows(data);
    } catch (e) { setError(formatApiError(e)); }
  };

  useEffect(() => {
    if (isAdmin) api.get("/customers").then(({ data }) => setCustomers(data));
  }, [isAdmin]);

  useEffect(() => {
    const t = setTimeout(fetchRows, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, kindFilter, selectedCustomer]);

  const openCreate = () => {
    setEditId(null);
    setForm(empty);
    setError("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      name: row.name || "",
      address: row.address || "",
      zip: row.zip || "",
      city: row.city || "",
      country: row.country || "CH",
      kind: row.kind || "both",
      notes: row.notes || "",
    });
    setError("");
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form };
      if (isAdmin && selectedCustomer && !editId) {
        payload.customer_id = selectedCustomer;
      }
      if (editId) {
        await api.put(`/address-book/${editId}`, payload);
      } else {
        if (isAdmin && !selectedCustomer) {
          setError("Bitte oben zuerst einen Kunden auswählen.");
          return;
        }
        await api.post("/address-book", payload);
      }
      setOpen(false);
      setEditId(null);
      setForm(empty);
      fetchRows();
    } catch (err) { setError(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Adresse wirklich aus dem Stamm entfernen?")) return;
    await api.delete(`/address-book/${id}`);
    fetchRows();
  };

  const KindBadge = ({ kind }) => {
    const map = {
      pickup:   { label: "Abholung",  bg: "rgba(126,58,242,0.10)", text: "#6B21A8", border: "rgba(126,58,242,0.30)" },
      delivery: { label: "Lieferung", bg: "rgba(34,197,94,0.10)",  text: "#15803D", border: "rgba(34,197,94,0.35)" },
      both:     { label: "Beide",     bg: "rgba(212,175,55,0.10)", text: "#FDE047", border: "rgba(212,175,55,0.30)" },
    };
    const s = map[kind] || map.both;
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-semibold tracking-wider uppercase border"
        style={{ background: s.bg, color: s.text, borderColor: s.border }}>
        {s.label}
      </span>
    );
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Stammdaten</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">Adressbuch</h1>
            <p className="text-sm text-[var(--text-3)] mt-1">
              {isAdmin ? "Adressstamm aller Kunden. Bitte oben einen Kunden wählen, um zu filtern oder Einträge zu ergänzen." : "Ihre häufig verwendeten Abhol- und Lieferadressen — werden bei jedem Auftrag automatisch ergänzt."}
            </p>
          </div>
          <button onClick={openCreate} data-testid="addr-add-button" className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2">
            <Plus size={16} strokeWidth={1.8} /> Neue Adresse
          </button>
        </div>

        {/* Filters */}
        <div className="mt-6 grid md:grid-cols-3 gap-3">
          <div className="relative md:col-span-2">
            <Search size={15} strokeWidth={1.5} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-3)] pointer-events-none" />
            <input
              data-testid="addr-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, Strasse, Ort suchen…"
              className="input-base !pl-11"
            />
          </div>
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="input-base" data-testid="addr-kind-filter">
            <option value="">Alle Typen</option>
            <option value="pickup">Nur Abholung</option>
            <option value="delivery">Nur Lieferung</option>
          </select>
        </div>

        {isAdmin && (
          <div className="mt-3">
            <label className="label-eyebrow block mb-2">Kunde wählen</label>
            <select value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)} data-testid="addr-customer-filter" className="input-base">
              <option value="">— Bitte Kunde auswählen —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {error && (
          <div className="mt-4 border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm">{error}</div>
        )}

        <div className="card mt-6 overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                <th className="text-left py-3 px-4">Name / Firma</th>
                <th className="text-left py-3 px-4">Adresse</th>
                <th className="text-left py-3 px-4">Typ</th>
                <th className="text-left py-3 px-4">Verwendet</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-[var(--text-3)]" data-testid="addr-empty">
                  {isAdmin && !selectedCustomer ? (
                    <div className="flex flex-col items-center gap-2">
                      <BookMarked size={24} className="text-[var(--text-3)]" />
                      Bitte oben einen Kunden auswählen.
                    </div>
                  ) : "Keine Einträge vorhanden. Erstellen Sie einen Auftrag oder fügen Sie hier manuell eine Adresse hinzu."}
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-2)] transition-colors" data-testid={`addr-row-${r.id}`}>
                  <td className="py-3 px-4">
                    <div className="text-[var(--text)]">{r.name}</div>
                  </td>
                  <td className="py-3 px-4 text-[var(--text)]">
                    <div className="flex items-start gap-2">
                      <MapPin size={13} className="text-[var(--text-3)] mt-0.5 shrink-0" />
                      <div>
                        <div>{r.address}</div>
                        <div className="text-[var(--text-3)] text-xs">{r.zip} {r.city}{r.country && r.country !== "CH" ? `, ${r.country}` : ""}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4"><KindBadge kind={r.kind} /></td>
                  <td className="py-3 px-4 font-mono text-[var(--text-2)]">
                    <div className="flex items-center gap-1.5"><Package size={12} className="text-[var(--text-3)]" /> {r.use_count || 0}×</div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => openEdit(r)} data-testid={`addr-edit-${r.id}`} className="text-[var(--text-3)] hover:text-[#D4AF37] mr-3"><Pencil size={16} strokeWidth={1.5} /></button>
                    <button onClick={() => del(r.id)} data-testid={`addr-delete-${r.id}`} className="text-[var(--text-3)] hover:text-red-700"><Trash2 size={16} strokeWidth={1.5} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-xl p-6 fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">{editId ? "Adresse bearbeiten" : "Neue Adresse"}</h2>
              <button onClick={() => setOpen(false)} className="text-[var(--text-3)] hover:text-[var(--text)]"><X size={18} /></button>
            </div>
            {error && <div className="border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm mb-4">{error}</div>}
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="label-eyebrow block mb-2">Name / Firma *</label>
                <input required data-testid="addr-form-name" className="input-base" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="z. B. Digitec Galaxus AG" />
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-2">Strasse / Hausnummer *</label>
                <input required data-testid="addr-form-address" className="input-base" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} />
              </div>
              <div><label className="label-eyebrow block mb-2">PLZ</label><input data-testid="addr-form-zip" className="input-base" value={form.zip} onChange={(e) => setForm({...form, zip: e.target.value})} /></div>
              <div><label className="label-eyebrow block mb-2">Ort</label><input data-testid="addr-form-city" className="input-base" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} /></div>
              <div>
                <label className="label-eyebrow block mb-2">Land</label>
                <select className="input-base" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})}>
                  <option value="CH">Schweiz (CH)</option>
                  <option value="DE">Deutschland (DE)</option>
                  <option value="AT">Österreich (AT)</option>
                  <option value="FR">Frankreich (FR)</option>
                  <option value="IT">Italien (IT)</option>
                  <option value="LI">Liechtenstein (LI)</option>
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Verwendung</label>
                <select data-testid="addr-form-kind" className="input-base" value={form.kind} onChange={(e) => setForm({...form, kind: e.target.value})}>
                  <option value="both">Abholung + Lieferung</option>
                  <option value="pickup">Nur Abholung</option>
                  <option value="delivery">Nur Lieferung</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-2">Notiz</label>
                <textarea rows={2} className="input-base" value={form.notes || ""} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="z. B. Filiale Nord · Rampe 3" />
              </div>
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-sm rounded-sm uppercase tracking-wider">Abbrechen</button>
                <button type="submit" data-testid="addr-form-submit" className="btn-gold px-4 py-2 text-sm rounded-sm uppercase tracking-wider">{editId ? "Speichern" : "Anlegen"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
